// This code is part of a React component that handles payment tracking for campaigns, including viewing agreements, uploading content links, generating invoices, and managing campaign states.

import React, { useEffect, useState } from "react";
import baseUrl from "../../api/universalApi";
import { useNavigate } from "react-router-dom";
import InvoiceView from "./InvoiceView/InvoiceView";
import ViewContract from "./ViewContract";
import { toast } from "react-hot-toast";

const initialSteps = [
  {
    title: "Campaign Created",
    section: "Campaign",
    apiKey: "campaign_created",
  },
  {
    title: "Agreement Created",
    section: "Agreement",
    apiKey: "agreement_created",
  },
  {
    title: "Agreement Signed",
    section: "Agreement",
    apiKey: "agreement_signed",
    escalation_count: 0,
    escalation: true,
    escalation_time: new Date(),
  },
  {
    title: "Campaign Live",
    section: "Campaign",
    apiKey: "campaign_live",
    action: "Upload Link",
  },
  { title: "Invoice Sent", section: "Invoice", apiKey: "invoice_sent" },
  {
    title: "Invoice Opened",
    section: "Invoice",
    apiKey: "invoice_opened",
    escalation_count: 0,
    escalation: true,
    escalation_time: new Date(),
  },
  {
    title: "Invoice Approved",
    section: "Invoice",
    apiKey: "invoice_approved",
    escalation_count: 0,
    escalation: true,
    escalation_time: new Date(),
  },
  {
    title: "Sent to Finance",
    section: "Invoice",
    apiKey: "invoice_sent_to_finance",

    escalation_count: 0,
    escalation: true,
    escalation_time: new Date(),
  },
  {
    title: "Processed",
    section: "Invoice",
    apiKey: "process_completed",
    escalation_count: 0,
    escalation: true,
    escalation_time: new Date(),
  },
  {
    title: "Paid to Creator",
    section: "Invoice",
    apiKey: "paid_to_creator",
    escalation_count: 0,
    escalation: true,
    escalation_time: new Date(),
  },
];

const PaymentTracking = ({
  creatorData,
  campaignDetails,
  fetchAllCampaigns,
}) => {
  const campaignId = campaignDetails?._id;
  const [steps, setSteps] = useState([]);
  const [campaignData, setCampaignData] = useState([]);
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [agreementData, setAgreementData] = useState(null);
  const [popup, setPopup] = useState(false);
  const [input, setInput] = useState("");
  const [Isinvoice, setIsInvoice] = useState(false);
  const [deletePopup, setDeletePopup] = useState(false);
  const [deleteCampaign, setDeleteCampaign] = useState("");
  const [contractPopup, setContractPopup] = useState(false);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const fetchPaymentTrackData = async (campaignId) => {
    try {
      const timelineRes = await fetch(
        `${baseUrl}/api/campaign/getPaymentTracking/${campaignId}`
      );
      if (!timelineRes.ok)
        throw new Error("Failed to fetch payment tracking data");

      const timelineData = await timelineRes.json();
      const mergedSteps = initialSteps.map((step, index) => {
        const entry = timelineData?.[step.apiKey];
        return {
          ...step,
          status: entry?.status ? "done" : "pending",
          date: entry?.timestamp
            ? new Date(entry.timestamp).toLocaleString(undefined, {
                month: "long",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })
            : null,
        };
      });

      const firstPendingIndex = mergedSteps.findIndex(
        (step) => step.status === "pending"
      );
      setCurrentStepIndex(
        firstPendingIndex === -1 ? mergedSteps.length : firstPendingIndex
      );
      setSteps(mergedSteps);
    } catch (error) {
      console.error("Error fetching payment tracking data:", error);
    }
  };

  const fetchCampaignById = async (campaignId) => {
    try {
      const response = await fetch(
        `${baseUrl}/api/campaign/fetchCampaignById/${campaignId}`
      );
      if (!response.ok) throw new Error("Failed to fetch campaign data");
      const data = await response.json();
      setActiveCampaign(data);
      setCampaignData((prev) => [...prev, data]);
    } catch (error) {
      console.error("Error fetching campaign by ID:", error);
    }
  };

  useEffect(() => {
    // console.log(activeCampaign);
    if (campaignId) {
      fetchPaymentTrackData(campaignId);
      fetchCampaignById(campaignId);
    }
  }, [campaignId]);

  const fetchAgreement = async (campaignId) => {
    try {
      const agreementRes = await fetch(
        `${baseUrl}/api/agreement/getAgreement/${campaignId}`
      );
      if (!agreementRes.ok) throw new Error("Failed to fetch agreement data");
      const agreementData = await agreementRes.json();

      if (agreementData) {
        setAgreementData(agreementData);
      }
    } catch (error) {
      console.error("Error fetching agreement data:", error);
    }
  };

  useEffect(() => {
    if (activeCampaign?._id) {
      fetchAgreement(activeCampaign._id);
    }
  }, [activeCampaign]);

  const navigate = useNavigate();
  const handleNavigate = () => {
    if (activeCampaign?._id) {
      navigate(`/create-contract/${activeCampaign._id}`);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInput((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeCam = (e) => {
    const { name, value } = e.target;
    setDeleteCampaign((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (currentStepIndex < 3) return alert("Previous steps are not completed.");

    const payload = {
      campaignId: activeCampaign._id,
      contentLink: input,
    };

    try {
      const res = await fetch(`${baseUrl}/api/campaign/uploadContentLink`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      setPopup(false);
      fetchAllCampaigns()
    } catch (error) {
      console.log("Error", error.message);
    }
  };

  const handlePaid = async (campaignId) => {
    if (currentStepIndex < initialSteps.length - 1)
      return alert("Previous steps are not completed.");

    try {
      const res = await fetch(`${baseUrl}/api/invoice/markAsPaid`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ campaign_id: campaignId }),
      });

      const data = await res.json();
      if (data.success) {
        toast.success("Marked as Paid Successfully");
        fetchPaymentTrackData(campaignId);
      } else {
        toast.error("Failed to mark as paid");
      }
    } catch (error) {
      console.error("Error marking as paid:", error);
    }
  };

  const handleDelete = async (campaign_id, campaign) => {
    if (campaign === deleteCampaign.delete) {
      try {
        const response = await fetch(
          `${baseUrl}/api/invoice/deleteCampaign/${campaign_id}`,
          { method: "DELETE" }
        );

        const data = await response.json();
        if (data.success) {
          toast.success("Deleted Successfully");
        } else {
          toast.error("Unable to Delete");
        }
        setDeletePopup(false);
        fetchAllCampaigns();
      } catch (error) {
        console.log(error);
      }
    } else {
      toast.error("Type Correct Campaign Name");
      return;
    }
  };

  const handleView = () => {
    setContractPopup(true);
  };
  return (
    // JSX output continues unchanged...
    <>
      <div
        className="min-h-screen bg-gray-50 flex flex-col items-center animate-fade-in"
        style={{ fontFamily: "Gilroy-Medium" }}
      >
        <div className="w-full bg-white p-4  flex flex-col gap-8">
          <div>
            <h2 className="text-2xl font-bold mb-1">Payment Tracking</h2>
            <p className="text-gray-500 text-sm mb-0">
              Monitor the progress of your invoices through each stage, from
              campaign creation to final payment.
            </p>
          </div>

          <div className="flex tp-flex-row w-full gap-8">
            <div className="w-1/2 xs:w-full">
              <ul className="relative pl-8">
                {steps.map((step, idx) => (
                  <li
                    key={idx}
                    className="relative flex items-start mb-8 last:mb-0"
                  >
                    <span
                      className={`relative z-10 flex items-center justify-center w-6 h-6 rounded-full border-2 shadow-sm mt-1 ${
                        step.status === "done"
                          ? "bg-white border-green-500 text-green-500"
                          : "bg-white border-gray-300 text-gray-300"
                      }`}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </span>
                    <div className="ml-6">
                      <div
                        className={`font-semibold ${
                          step.status === "done"
                            ? "text-gray-800"
                            : "text-gray-400"
                        } `}
                      >
                        {step.title}
                        {"  "}
                      </div>
                      {step.date && (
                        <div className="text-gray-500 text-sm">{step.date}</div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col items-center gap-6 w-1/2 xs:w-full">
              <div className="bg-white border rounded-xl shadow-sm px-6 py-6 flex flex-col gap-3 items-start w-full max-w-md">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">
                    Agreement Preview
                  </h3>
                  <p className="text-sm text-gray-500">
                    Review the Terms and Conditions of the agreement
                  </p>
                </div>
                {agreementData?.rep_email ? (
                  <div className="flex flex-row gap-2 w-full">
                    <button
                      className="w-full py-2 border rounded-md bg-green-500 text-sm text-gray-800 font-medium hover:bg-gray-100 transition"
                      onClick={() => setContractPopup(true)}
                    >
                      View Agreement
                    </button>
                  </div>
                ) : (
                  <button
                    className="w-full py-2 border rounded-md text-sm text-gray-800 font-medium hover:bg-gray-100 transition"
                    onClick={handleNavigate}
                  >
                    Create Contract
                  </button>
                )}

                {contractPopup && (
                  <>
                    <div className="fixed inset-0 bg-gray-400 bg-opacity-80  z-40"></div>
                    <div className="fixed inset-0 z-50 flex items-center justify-center border-2  bg-opacity-30">
                      <div className="bg-white w-full max-w-3xl rounded-lg border-2 border-gray-600 shadow-lg p-6 relative">
                        <button
                          onClick={() => setContractPopup(false)}
                          className="absolute top-2 right-2 text-gray-500 hover:text-red-600 text-xl font-bold"
                        >
                          &times;
                        </button>
                        <ViewContract agreementData={agreementData} />
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="bg-white border rounded-xl shadow-sm px-6 py-6 flex flex-col gap-3 items-start w-full max-w-md">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">
                    Upload Content Link
                  </h3>
                  <p className="text-sm text-gray-500">
                    Upload Content Link to make campaign Live
                  </p>
                </div>

                {campaignDetails?.contentLink ? (
                  <>
                    <button className="w-full py-2 border rounded-md text-sm bg-green-400 font-medium hover:bg-white transition">
                      <a
                        href={campaignDetails?.contentLink}
                        target="_blank"
                        className="text-black"
                        style={{ textDecoration: "none" }}
                      >
                        View content
                      </a>
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="w-full py-2 border rounded-md text-sm text-gray-800 font-medium hover:bg-gray-100 transition"
                      onClick={() => setPopup(true)}
                    >
                      Upload link
                    </button>
                  </>
                )}
              </div>

              <div className="bg-white border rounded-xl shadow-sm px-6 py-6 flex flex-col gap-3 items-start w-full max-w-md">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">
                    Invoice Preview
                  </h3>
                  <p className="text-sm text-gray-500">
                    View Generated Invoice details
                  </p>
                </div>

                {campaignDetails?.invoice?.rep_email ? (
                  <>
                    <button
                      className="w-full py-2 border rounded-md text-sm bg-green-400 font-medium hover:bg-white transition"
                      onClick={() => {
                        setIsInvoice(true);
                      }}
                    >
                      View Invoice
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="w-full py-2 border rounded-md text-sm text-gray-800 font-medium hover:bg-gray-100 transition"
                      onClick={() => {
                        setIsInvoice(true);
                      }}
                    >
                      Generate Invoice
                    </button>
                  </>
                )}
              </div>

              <div className="flex gap-4 mt-4">
                <button
                  className="border px-3 py-1 rounded-full text-sm font-medium text-gray-700 hover:bg-gray-100"
                  onClick={() => {
                    handlePaid(activeCampaign._id);
                  }}
                >
                  Mark as Paid
                </button>

                <div>
                  <button
                    className=" bg-red-500 text-white px-3 py-1 rounded-full text-sm  hover:opacity-90"
                    onClick={() => {
                      setDeletePopup(true);
                    }}
                  >
                    Delete Campaign
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <>
        {popup && (
          <>
            <div className="fixed inset-0 bg-gray-400 bg-opacity-80  z-40"></div>
            <div className="fixed inset-0 z-50 flex items-center justify-center  bg-opacity-30">
              <div className="bg-white w-full max-w-3xl rounded-lg shadow-lg p-6 relative">
                <button
                  onClick={() => setPopup(false)}
                  className="absolute top-2 right-2 text-gray-500 hover:text-red-600 text-xl font-bold"
                >
                  &times;
                </button>
                <div className="gap-2 flex flex-col">
                  <div>
                    <label>Upload Content Link</label>
                  </div>
                  <div>
                    <input
                      type="text"
                      name="contentLink"
                      placeholder="Enter Content Link"
                      value={input.contentLink}
                      onChange={handleChange}
                      className="w-full border p-2 rounded"
                    />
                  </div>
                  <div>
                    <button
                      className="bg-green-300 rounded-full py-1 px-3"
                      onClick={() => handleSubmit()}
                    >
                      Submit
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {Isinvoice && (
          <>
            <div className="fixed inset-0 bg-gray-400 bg-opacity-80  z-40"></div>
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-opacity-30 overflow-auto h-screen">
              <div className="bg-white w-full m-6 border-3 border-gray-400  rounded-lg shadow-lg p-6 relative">
                <button
                  onClick={() => setIsInvoice(false)}
                  className="absolute top-2 right-2 text-gray-500 hover:text-red-600 text-xl font-bold"
                >
                  &times;
                </button>
                {
                  <InvoiceView
                    campaign={campaignData}
                    contract={agreementData}
                    setIsInvoice={setIsInvoice}
                    setPopup={setPopup}
                  />
                }
              </div>
            </div>
          </>
        )}
      </>

      {/* Delete Campaign Component */}
      <>
        {deletePopup && (
          <>
            <div className="fixed inset-0 bg-gray-400 bg-opacity-80  z-40"></div>
            <div className="fixed inset-0 z-50 flex items-center justify-center  bg-opacity-30">
              <div className="bg-white w-full max-w-3xl rounded-lg shadow-lg p-6 relative">
                <button
                  onClick={() => setDeletePopup(false)}
                  className="absolute top-2 right-2 text-gray-500 hover:text-red-600 text-xl font-bold"
                >
                  &times;
                </button>
                <p>
                  Type "<b>{activeCampaign?.campaignTitle}</b>" to confirm to
                  delete this campaign{" "}
                </p>
                <input
                  type="text"
                  onChange={handleChangeCam}
                  name="delete"
                  value={deleteCampaign.delete || ""}
                  placeholder="Type Campaign Name "
                  className="w-full border p-2 rounded mb-3"
                />

                <button
                  className="bg-green-300 rounded-full py-1 px-3"
                  onClick={() =>
                    handleDelete(
                      activeCampaign?._id,
                      activeCampaign?.campaignTitle
                    )
                  }
                >
                  Submit
                </button>
              </div>
            </div>
          </>
        )}
      </>
    </>
  );
};

export default PaymentTracking;
